import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [me,           setMe]           = useState(null);
  const [token,        setToken]        = useState(() => sessionStorage.getItem("iws_token"));
  const [loading,      setLoading]      = useState(true);
  const [impersonating,setImpersonating]= useState(null);

  const logout = useCallback(() => {
    const company = sessionStorage.getItem("iws_company");
    sessionStorage.removeItem("iws_token");
    sessionStorage.removeItem("iws_company");
    setToken(null); setMe(null); setImpersonating(null);
    if (company) window.location.href = `/${company}/login`;
    else window.location.href = "/portal/login";
  }, []);

  // Now accepts company_code as first argument
  const login = useCallback(async (company_code, employee_id, password) => {
    const res = await api.post("/portal/login", { company_code, employee_id, password });
    sessionStorage.setItem("iws_token", res.token);
    if (company_code) sessionStorage.setItem("iws_company", company_code.toLowerCase());
    setToken(res.token);
    setMe(res.employee);
    return res;
  }, []);

  const impersonate = useCallback(async (emp_code) => {
    const res = await api.post(`/portal/admin/impersonate/${emp_code}`);
    setImpersonating({ token, me });
    sessionStorage.setItem("iws_token", res.token);
    if (company_code) sessionStorage.setItem("iws_company", company_code.toLowerCase());
    setToken(res.token);
    setMe(res.employee);
  }, [token, me]);

  const exitImpersonate = useCallback(() => {
    if (!impersonating) return;
    sessionStorage.setItem("iws_token", impersonating.token);
    setToken(impersonating.token);
    setMe(impersonating.me);
    setImpersonating(null);
  }, [impersonating]);

  const refreshMe = useCallback(() => {
    api.get("/portal/me").then(d => setMe(d)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    // Always fetch fresh from server so feature changes take effect immediately
    api.get("/portal/me")
      .then(emp => setMe(emp))
      .catch(() => logout())
      .finally(() => setLoading(false));
  }, [token]);

  // Refresh me every 5 minutes to pick up feature/subscription changes
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      api.get("/portal/me").then(emp => setMe(emp)).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const has = (feat) => {
    if (!me) return false;
    // features object always wins - includes company overrides
    if (me.features && Object.prototype.hasOwnProperty.call(me.features, feat)) {
      return !!me.features[feat];
    }
    // fallback: admin gets everything not explicitly listed
    return me.role === "admin";
  };

  const anyAdmin = () => {
    if (!me) return false;
    if (me.role === "admin") return true;
    return !!(me.features?.timesheet_admin) || me.role === "hr";
  };

  return (
    <AuthCtx.Provider value={{ me, token, loading, impersonating,
      login, logout, impersonate, exitImpersonate, has, anyAdmin, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
}
